import { useState, useEffect, useCallback } from "react";
import { useFormik } from "formik";
import { useSelector } from "react-redux";
import { useLocation } from "react-router";
import { toast } from "react-toastify";

// Components
import {
  DragAndDropUpload,
  Button,
  CentralModal,
  Card,
  Header,
  Body,
  Textarea,
  ToggleSwitch,
  DualRangeSlider,
  Checkbox
} from "../../../../components/UI";
import { Label, Paragraph, Heading2 } from "../../../../components/typography";
import GeneralSettingsListModal from "./GeneralSettingsListModal";

// Builders
import * as buttonDesignType from "../../../../builders/buttonDesign.types";

// Calls
import {
  branchActivationRequest,
  fetchBranchGeneralSettings,
  updateBranchGeneralSettingsRequest
} from "../../../../api/calls/general-branch-settings.call";

// Form Models
import { BranchGeneralSettingsFormModel } from "../../../../models/app/forms";

// Constants
import * as modalTypes from "../../../../constants/generalSettingsModal.constants";

// Styles
import { GeneralSettingsFormStyles } from "./generalSettingsForm.styles";

const GeneralSettingsForm = ({ branchId }) => {
  const { systemComponents } = useSelector(state => state.app);
  const { search } = useLocation();
  const [modalType, setModalType] = useState(undefined);
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [initialGeneralSettingsValue, setInitialGeneralSettingsValue] =
    useState(new BranchGeneralSettingsFormModel());

  const getBranchGeneralSettings = useCallback(async () => {
    const response = await fetchBranchGeneralSettings(branchId);
    if (response.hasError) {
      toast.error(response.error.message);
      return;
    }
    setInitialGeneralSettingsValue(
      new BranchGeneralSettingsFormModel(
        response.branch.generateBranchGeneralSettingsForm()
      )
    );
    setSettingsLoaded(true);
  }, [branchId]);

  useEffect(() => {
    getBranchGeneralSettings();
  }, [getBranchGeneralSettings]);

  const updateAndSync = async (payload, toastMessage) => {
    const response = await updateBranchGeneralSettingsRequest(
      payload,
      initialGeneralSettingsValue,
      branchId
    );
    if (response.hasError) {
      toast.error(response.error.message);
      return;
    }
    toast.success(toastMessage);
    setInitialGeneralSettingsValue(
      new BranchGeneralSettingsFormModel(
        response.branch.generateBranchGeneralSettingsForm()
      )
    );
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: initialGeneralSettingsValue,
    onSubmit: () => {}
  });

  const { values, setFieldValue, handleBlur, handleChange } = formik;

  // Onboarding deep-links here as ?tab=GENERAL_SETTINGS&setup=<step> (steps 5
  // and 7). Ping the card that step is about — but only while it is actually
  // outstanding, otherwise the glow is just noise on a done step. Gated on
  // settingsLoaded so we neither ping on the empty pre-fetch form nor race
  // GeneralBranchSettings' own scrollTo(0, 0) on mount.
  const setupTarget = new URLSearchParams(search).get("setup");
  const hasEquipment =
    values.truckManufacturers.length > 0 ||
    values.trailerManufacturers.length > 0 ||
    values.trailerTypes.length > 0;

  let pingCard = null;
  if (settingsLoaded) {
    if (setupTarget === "equipment" && !hasEquipment) pingCard = "equipment";
    else if (setupTarget === "live" && !values.isActive) pingCard = "live";
  }

  useEffect(() => {
    if (!pingCard) return;
    // Card doesn't forward refs, and only one card pings at a time.
    const card = document.querySelector(".tf-setup-ping");
    if (card) card.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [pingCard]);

  // Dirty detection per section
  const isDescriptionDirty =
    values.description !== initialGeneralSettingsValue.description;
  const isSpecialityDirty =
    values.speciality !== initialGeneralSettingsValue.speciality;
  const isPricingDirty =
    Number(values.hourlyPriceFrom) !== Number(initialGeneralSettingsValue.hourlyPriceFrom) ||
    Number(values.hourlyPriceTo) !== Number(initialGeneralSettingsValue.hourlyPriceTo);
  const isAmenitiesDirty =
    values.hasParking !== initialGeneralSettingsValue.hasParking ||
    JSON.stringify(values.paymentTypes.map(pt => pt.key).sort()) !==
      JSON.stringify(
        initialGeneralSettingsValue.paymentTypes.map(pt => pt.key).sort()
      );

  // Per-section save handlers
  const saveDescription = () =>
    updateAndSync(
      { description: values.description },
      "Description successfully updated."
    );

  const saveSpeciality = () =>
    updateAndSync(
      { speciality: values.speciality },
      "Speciality successfully updated."
    );

  const savePricing = () =>
    updateAndSync(
      {
        hourlyPriceFrom: Number(values.hourlyPriceFrom),
        hourlyPriceTo: Number(values.hourlyPriceTo)
      },
      "Pricing successfully updated."
    );

  const saveAmenities = async () => {
    const payload = {};
    if (values.hasParking !== initialGeneralSettingsValue.hasParking) {
      payload.hasParking = values.hasParking;
    }
    const currentKeys = values.paymentTypes.map(pt => pt.key).sort();
    const initialKeys = initialGeneralSettingsValue.paymentTypes
      .map(pt => pt.key)
      .sort();
    if (JSON.stringify(currentKeys) !== JSON.stringify(initialKeys)) {
      payload.supportedPaymentTypes = values.paymentTypes.map(pt => pt.key);
    }
    await updateAndSync(payload, "Amenities successfully updated.");
  };

  // Immediate save handlers (unchanged sections)
  const saveModalChanges = async value => {
    switch (modalType) {
      case modalTypes.SUPPORTED_TRUCKS:
        const supportedTrucksResponse =
          await updateBranchGeneralSettingsRequest(
            { supportedTrucks: value.map(truck => truck.id) },
            values,
            branchId
          );
        if (supportedTrucksResponse.hasError) {
          toast.error(supportedTrucksResponse.error.message);
          return;
        }
        toast.success("Supported trucks successfully updated.");
        setInitialGeneralSettingsValue(
          new BranchGeneralSettingsFormModel(
            supportedTrucksResponse.branch.generateBranchGeneralSettingsForm()
          )
        );
        break;
      case modalTypes.SUPPORTED_TRAILERS:
        const supportedTrailersResponse =
          await updateBranchGeneralSettingsRequest(
            { supportedTrailers: value.map(trailer => trailer.id) },
            values,
            branchId
          );
        if (supportedTrailersResponse.hasError) {
          toast.error(supportedTrailersResponse.error.message);
          return;
        }
        toast.success("Supported trailers successfully updated.");
        setInitialGeneralSettingsValue(
          new BranchGeneralSettingsFormModel(
            supportedTrailersResponse.branch.generateBranchGeneralSettingsForm()
          )
        );
        break;
      case modalTypes.SUPPORTED_TRAILER_TYPES:
        const supportedTrailerTypesResponse =
          await updateBranchGeneralSettingsRequest(
            { supportedTrailerTypes: value.map(trailerType => trailerType.id) },
            values,
            branchId
          );
        if (supportedTrailerTypesResponse.hasError) {
          toast.error(supportedTrailerTypesResponse.error.message);
          return;
        }
        toast.success("Supported trailer types successfully updated.");
        setInitialGeneralSettingsValue(
          new BranchGeneralSettingsFormModel(
            supportedTrailerTypesResponse.branch.generateBranchGeneralSettingsForm()
          )
        );
        break;
      case modalTypes.SUPPORTED_PAYMENT_TYPES:
        const supportedPaymentTypesResponse =
          await updateBranchGeneralSettingsRequest(
            {
              supportedPaymentTypes: value.map(paymentType => paymentType.key)
            },
            values,
            branchId
          );
        if (supportedPaymentTypesResponse.hasError) {
          toast.error(supportedPaymentTypesResponse.error.message);
          return;
        }
        toast.success("Supported payment options successfully updated.");
        setInitialGeneralSettingsValue(
          new BranchGeneralSettingsFormModel(
            supportedPaymentTypesResponse.branch.generateBranchGeneralSettingsForm()
          )
        );
        break;
      default:
        break;
    }

    setModalType(undefined);
  };

  const handleImagesChange = async (name, urls) => {
    const response = await updateBranchGeneralSettingsRequest(
      { [name]: urls },
      values,
      branchId
    );
    if (response.hasError) {
      toast.error(response.error.message);
      return;
    }
    toast.success("Gallery successfully updated.");
    setInitialGeneralSettingsValue(
      new BranchGeneralSettingsFormModel(
        response.branch.generateBranchGeneralSettingsForm()
      )
    );
  };

  const handleBranchActivation = async (name, value) => {
    const response = await branchActivationRequest({ [name]: value }, branchId);
    if (response.hasError) {
      toast.error(response.error.message);
      return;
    }
    toast.success("Branch activation successfully updated.");
    setFieldValue(name, value);
  };

  const handleAutoAcceptShopToggle = async (name, value) => {
    setFieldValue(name, value);
    await updateAndSync(
      { autoAcceptShop: value },
      value
        ? "Shop service requests will be auto-accepted."
        : "Shop service requests will require manual acceptance."
    );
  };

  // Local-only amenities handlers
  const handleParkingToggle = (name, value) => {
    setFieldValue(name, value);
  };

  const handlePaymentTypeToggle = paymentType => {
    const isSelected = values.paymentTypes.some(
      pt => pt.key === paymentType.key
    );
    const updatedTypes = isSelected
      ? values.paymentTypes.filter(pt => pt.key !== paymentType.key)
      : [...values.paymentTypes, paymentType];
    setFieldValue("paymentTypes", updatedTypes);
  };

  return (
    <GeneralSettingsFormStyles>
      <form onSubmit={e => e.preventDefault()}>
        {/* Card 1 - Branch Visibility */}
        <Card rounded className={pingCard === "live" ? "tf-setup-ping" : undefined}>
          <Header>
            <Heading2>BRANCH VISIBILITY</Heading2>
          </Header>
          <Body>
            <div className="fieldInline">
              <Label>Show This Business In The Application?</Label>
              <ToggleSwitch
                name="isActive"
                value={values.isActive}
                yesText="Yes"
                noText="No"
                handleChange={handleBranchActivation}
              />
            </div>
          </Body>
        </Card>

        {/* Card 2 - Booking & Acceptance */}
        <Card rounded>
          <Header>
            <Heading2>BOOKING & ACCEPTANCE</Heading2>
          </Header>
          <Body>
            <div className="fieldInline">
              <Label>Auto-Accept Shop Service Requests?</Label>
              <ToggleSwitch
                name="autoAcceptShop"
                value={values.autoAcceptShop}
                yesText="Yes"
                noText="No"
                handleChange={handleAutoAcceptShopToggle}
              />
            </div>
            <Paragraph style={{ marginTop: 8 }}>
              When enabled, shop (on-site) service requests are immediately
              accepted without manual review. Recommended when intake desk
              capacity is predictable. Mobile requests always require manual
              acceptance.
            </Paragraph>
          </Body>
        </Card>

        {/* Card 3 - Description */}
        <Card rounded>
          <Header>
            <div className="cardHeaderRow">
              <Heading2>DESCRIPTION</Heading2>
              {isDescriptionDirty && (
                <Button
                  className="sectionSaveBtn"
                  onClick={saveDescription}
                  buttonDesignType={buttonDesignType.PRIMARY}
                >
                  Save Changes
                </Button>
              )}
            </div>
          </Header>
          <Body>
            <div className="fieldGroup">
              <Label>Branch Description</Label>
              <Textarea
                className="branchDescription"
                value={values.description}
                name="description"
                onBlur={handleBlur}
                onChange={handleChange}
                placeholder="Describe this branch"
              />
            </div>
          </Body>
        </Card>

        {/* Card 3 - Speciality */}
        <Card rounded>
          <Header>
            <div className="cardHeaderRow">
              <Heading2>SPECIALITY</Heading2>
              {isSpecialityDirty && (
                <Button
                  className="sectionSaveBtn"
                  onClick={saveSpeciality}
                  buttonDesignType={buttonDesignType.PRIMARY}
                >
                  Save Changes
                </Button>
              )}
            </div>
          </Header>
          <Body>
            <div className="fieldGroup">
              <Label>Branch Speciality</Label>
              <Textarea
                className="branchSpeciality"
                value={values.speciality}
                name="speciality"
                onBlur={handleBlur}
                onChange={handleChange}
                placeholder="What are you specially good at"
              />
            </div>
          </Body>
        </Card>

        {/* Card 4 - Supported Equipment */}
        <Card
          rounded
          className={pingCard === "equipment" ? "tf-setup-ping" : undefined}
        >
          <Header>
            <Heading2>SUPPORTED EQUIPMENT</Heading2>
          </Header>
          <Body>
            <div className="equipmentButtons">
              <Button
                onClick={() => setModalType(modalTypes.SUPPORTED_TRUCKS)}
                buttonDesignType={buttonDesignType.SECONDARY}
              >
                View Supported Trucks
              </Button>
              <Button
                onClick={() => setModalType(modalTypes.SUPPORTED_TRAILERS)}
                buttonDesignType={buttonDesignType.SECONDARY}
              >
                View Supported Trailers
              </Button>
              <Button
                onClick={() => setModalType(modalTypes.SUPPORTED_TRAILER_TYPES)}
                buttonDesignType={buttonDesignType.SECONDARY}
              >
                View Supported Trailer Types
              </Button>
            </div>
          </Body>
        </Card>

        {/* Card 5 - Pricing */}
        <Card rounded>
          <Header>
            <div className="cardHeaderRow">
              <Heading2>PRICING</Heading2>
              {isPricingDirty && (
                <Button
                  className="sectionSaveBtn"
                  onClick={savePricing}
                  buttonDesignType={buttonDesignType.PRIMARY}
                >
                  Save Changes
                </Button>
              )}
            </div>
          </Header>
          <Body>
            <div className="fieldGroup">
              <Label>Branch Price Range</Label>
              <DualRangeSlider
                min={0}
                max={500}
                step={5}
                valueFrom={values.hourlyPriceFrom}
                valueTo={values.hourlyPriceTo}
                nameFrom="hourlyPriceFrom"
                nameTo="hourlyPriceTo"
                onChange={handleChange}
                onBlur={handleBlur}
              />
            </div>
          </Body>
        </Card>

        {/* Card 6 - Amenities */}
        <Card rounded>
          <Header>
            <div className="cardHeaderRow">
              <Heading2>AMENITIES</Heading2>
              {isAmenitiesDirty && (
                <Button
                  className="sectionSaveBtn"
                  onClick={saveAmenities}
                  buttonDesignType={buttonDesignType.PRIMARY}
                >
                  Save Changes
                </Button>
              )}
            </div>
          </Header>
          <Body>
            <div className="fieldCompact">
              <Label>Has Parking?</Label>
              <ToggleSwitch
                name="hasParking"
                value={values.hasParking}
                yesText="Yes"
                noText="No"
                handleChange={handleParkingToggle}
              />
            </div>
            <div className="fieldGroup" style={{ marginTop: 16 }}>
              <Label>Payment Options</Label>
              <div className="amenitiesCheckboxes">
                {systemComponents.paymentTypes.map(pt => (
                  <Checkbox
                    key={pt.key}
                    label={pt.name}
                    value={values.paymentTypes.some(
                      selected => selected.key === pt.key
                    )}
                    handleChange={() => handlePaymentTypeToggle(pt)}
                  />
                ))}
              </div>
            </div>
          </Body>
        </Card>

        {/* Card 7 - Gallery */}
        <Card rounded>
          <Header>
            <Heading2>GALLERY</Heading2>
          </Header>
          <Body>
            <Paragraph className="dragAndDropText">
              Please browse for images or drag & drop them. Images must be
              minimum 1080 pixels wide. Max 3MB in size. Must be type: jpg,
              jpeg, png or gif.
            </Paragraph>
            <DragAndDropUpload
              images={values.images}
              name="images"
              handleChange={handleImagesChange}
            />
          </Body>
        </Card>

        {modalType && (
          <CentralModal
            width={680}
            closeModal={() => setModalType(undefined)}
          >
            <Card className="card">
              <GeneralSettingsListModal
                truckOptions={systemComponents.truckManufacturers}
                trailerOptions={systemComponents.trailerManufacturers}
                trailerTypeOptions={systemComponents.trailerTypes}
                paymentOptions={systemComponents.paymentTypes}
                trucks={values.truckManufacturers}
                trailers={values.trailerManufacturers}
                trailerTypes={values.trailerTypes}
                paymentTypes={values.paymentTypes}
                modalType={modalType}
                saveModalChanges={saveModalChanges}
                closeModal={() => setModalType(undefined)}
              />
            </Card>
          </CentralModal>
        )}
      </form>
    </GeneralSettingsFormStyles>
  );
};

export default GeneralSettingsForm;
